import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { WorkspaceSessionService } from '@crowdctrl/shared';
import { Session } from '@crowdctrl/core';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SessionContextService {
  private workspaceSessionService = inject(WorkspaceSessionService);

  private sessionSubject = new BehaviorSubject<Session | null>(null);
  private sessionIdSubject = new BehaviorSubject<string | null>(null);
  private workspaceIdSubject = new BehaviorSubject<string | null>(null);
  private tenantIdSubject = new BehaviorSubject<string | null>(null);
  private loading$: Observable<Session | null> | null = null;

  session$ = this.sessionSubject.asObservable();
  sessionId$ = this.sessionIdSubject.asObservable();
  workspaceId$ = this.workspaceIdSubject.asObservable();

  getSessionValue(): Session | null {
    return this.sessionSubject.value;
  }

  ensureSession$(
    tenantId: string,
    workspaceId: string,
    sessionId: string,
    forceRefresh: boolean = false,
  ): Observable<Session | null> {
    const idsMatch =
      this.tenantIdSubject.value === tenantId &&
      this.workspaceIdSubject.value === workspaceId &&
      this.sessionIdSubject.value === sessionId;

    if (!idsMatch) {
      this.sessionSubject.next(null);
    }

    if (!forceRefresh && idsMatch && this.sessionSubject.value) {
      return of(this.sessionSubject.value);
    }

    if (!forceRefresh && idsMatch && this.loading$) {
      return this.loading$;
    }

    this.tenantIdSubject.next(tenantId);
    this.workspaceIdSubject.next(workspaceId);
    this.sessionIdSubject.next(sessionId);

    const request$ = this.workspaceSessionService.getSession(tenantId, workspaceId, sessionId).pipe(
      map((response) => response?.session || null),
      tap((session) => {
        this.sessionSubject.next(session);
      }),
      finalize(() => {
        if (this.loading$ === request$) {
          this.loading$ = null;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.loading$ = request$;
    return request$;
  }

  async ensureSession(
    tenantId: string,
    workspaceId: string,
    sessionId: string,
    forceRefresh: boolean = false,
  ): Promise<Session | null> {
    return await firstValueFrom(
      this.ensureSession$(tenantId, workspaceId, sessionId, forceRefresh),
    );
  }

  setSession(session: Session | null): void {
    this.sessionSubject.next(session);
    if (session?.id) {
      this.sessionIdSubject.next(session.id);
    }
  }

  clear(): void {
    this.sessionSubject.next(null);
    this.sessionIdSubject.next(null);
    this.workspaceIdSubject.next(null);
    this.tenantIdSubject.next(null);
    this.loading$ = null;
  }
}
